import { Injectable, signal, computed } from '@angular/core';
import { Color } from '@iplab/ngx-color-picker';

export interface ThemeVariable {
  name: string;
  value: string;
  originalValue: string;
  group: string;
  isColor: boolean;
  isCustom?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeEditorService {
  private STORAGE_KEY = 'ngx-theme-editor-variables';

  private initialData: Record<string, string> = {
    '--theme-50': '#FFF4F2FF',
    '--theme-100': '#FFE6E0FF',
    '--theme-200': '#FFCCBEFF',
    '--theme-300': '#FFB59EFF',
    '--theme-400': '#FF9972FF',
    '--theme-500': '#FF7A26',
    '--theme-600': '#CE5D00FF',
    '--theme-700': '#9C4500FF',
    '--theme-800': '#682C00FF',
    '--theme-900': '#3C1600FF',
    '--theme-950': '#2A0D00FF',
    '--theme-transparent-05': '#FF7A260D',
    '--theme-transparent-10': '#FF7A261A',
    '--theme-transparent-20': '#FF7A2633',
    '--theme-transparent-30': '#FF7A264D',
    '--theme-transparent-40': '#FF7A2666',
    '--theme-transparent-50': '#FF7A2680',
    '--theme-transparent-60': '#FF7A2699',
    '--theme-transparent-70': '#FF7A26B2',
    '--theme-transparent-80': '#FF7A26CC',
    '--theme-transparent-90': '#FF7A26E5',
    '--theme-transparent-100': '#FF7A26FF',

    '--neutral-50': '#FAFAFAFF',
    '--neutral-100': '#F5F5F5FF',
    '--neutral-200': '#E5E5E5FF',
    '--neutral-300': '#D4D4D4FF',
    '--neutral-400': '#A3A3A3FF',
    '--neutral-500': '#737373FF',
    '--neutral-600': '#525252FF',
    '--neutral-700': '#404040FF',
    '--neutral-800': '#262626FF',
    '--neutral-900': '#171717FF',
    '--neutral-950': '#0A0A0AFF',
    '--neutral-transparent-05': '#7373730D',
    '--neutral-transparent-10': '#7373731A',
    '--neutral-transparent-20': '#73737333',
    '--neutral-transparent-30': '#7373734D',
    '--neutral-transparent-40': '#73737366',
    '--neutral-transparent-50': '#73737380',
    '--neutral-transparent-60': '#73737399',
    '--neutral-transparent-70': '#737373B2',
    '--neutral-transparent-80': '#737373CC',
    '--neutral-transparent-90': '#737373E5',
    '--neutral-transparent-100': '#737373FF',
    '--white-1000': '#FFFFFFFF',
    '--white-transparent-05': '#FFFFFF0D',
    '--white-transparent-10': '#FFFFFF1A',
    '--white-transparent-20': '#FFFFFF33',
    '--white-transparent-30': '#FFFFFF4D',
    '--white-transparent-40': '#FFFFFF66',
    '--white-transparent-50': '#FFFFFF80',
    '--white-transparent-60': '#FFFFFF99',
    '--white-transparent-70': '#FFFFFFB2',
    '--white-transparent-80': '#FFFFFFCC',
    '--white-transparent-90': '#FFFFFFE5',
    '--white-transparent-100': '#FFFFFFFF',
    '--black-1000': '#000000FF',
    '--black-transparent-05': '#0000000D',
    '--black-transparent-10': '#0000001A',
    '--black-transparent-20': '#00000033',
    '--black-transparent-30': '#0000004D',
    '--black-transparent-40': '#00000066',
    '--black-transparent-50': '#00000080',
    '--black-transparent-60': '#00000099',
    '--black-transparent-70': '#000000B2',
    '--black-transparent-80': '#000000CC',
    '--black-transparent-90': '#000000E5',
    '--black-transparent-100': '#000000FF',

    // '--gray-05': '#F9FAFBFF',
    // '--gray-10': '#F3F4F6FF',
    // '--gray-200': '#E5E7EBFF',
    // '--gray-300': '#D1D5DBFF',
    // '--gray-400': '#9CA3AFFF',
    // '--gray-500': '#6B7280FF',
    // '--gray-600': '#4B5563FF',
    // '--gray-700': '#374151FF',
    // '--gray-800': '#1F2937FF',
    // '--gray-900': '#111827FF',
    // '--gray-1000': '#030712FF',
    // '--gray-transparent-50': '#6B72800D',
    // '--gray-transparent-100': '#6B72801A',
    // '--gray-transparent-200': '#6B728033',
    // '--gray-transparent-300': '#6B72804D',
    // '--gray-transparent-40': '#6B728066',
    // '--gray-transparent-500': '#6B728080',
    // '--gray-transparent-60': '#6B728099',
    // '--gray-transparent-70': '#6B7280B2',
    // '--gray-transparent-80': '#6B7280CC',
    // '--gray-transparent-90': '#6B7280E5',
    // '--gray-transparent-950': '#6B7280FF',
    // '--red-50': '#FEF2F2FF',
    // '--red-100': '#FEE2E2FF',
    // '--red-200': '#FECACAFF',
    // '--red-300': '#FCA5A5FF',
    // '--red-400': '#F87171FF',
    // '--red-500': '#EF4444FF',
    // '--red-600': '#DC2626FF',
    // '--red-700': '#B91C1CFF',
    // '--red-800': '#991B1BFF',
    // '--red-900': '#7F1D1DFF',
    // '--red-950': '#450A0AFF',
    // '--red-transparent-05': '#EF44440D',
    // '--orange-50': '#FFF7EDFF',
    // '--orange-100': '#FFEDD5FF',
    // '--orange-200': '#FED7AAFF',
    // '--orange-300': '#FDBA74FF',
    // '--orange-400': '#FB923CFF',
    // '--orange-500': '#F97316FF',
    // '--orange-600': '#EA580CFF',
    // '--orange-700': '#C2410CFF',
    // '--orange-800': '#9A3412FF',
    // '--orange-900': '#7C2D12FF',
    // '--orange-950': '#431407FF',
    // '--red-transparent-10': '#EF44441A',
    // '--red-transparent-20': '#EF444433',
    // '--red-transparent-30': '#EF44444D',
    // '--red-transparent-40': '#EF444466',
    // '--red-transparent-50': '#EF444480',
    // '--red-transparent-60': '#EF444499',
    // '--red-transparent-70': '#EF4444B2',
    // '--red-transparent-80': '#EF4444CC',
    // '--red-transparent-90': '#EF4444E5',
    // '--red-transparent-100': '#EF4444FF',
    // '--orange-transparent-05': '#F973160D',
    // '--orange-transparent-10': '#F973161A',
    // '--orange-transparent-20': '#F9731633',
    // '--orange-transparent-30': '#F973164D',
    // '--orange-transparent-40': '#F9731666',
    // '--orange-transparent-50': '#F9731680',
    // '--orange-transparent-60': '#F9731699',
    // '--orange-transparent-70': '#F97316B2',
    // '--orange-transparent-80': '#F97316CC',
    // '--orange-transparent-90': '#F97316E5',
    // '--orange-transparent-100': '#F97316FF',
    // '--amber-50': '#FFFBEBFF',
    // '--amber-transparent-05': '#F59E0B0D',
    // '--amber-transparent-10': '#F59E0B1A',
    // '--amber-transparent-20': '#F59E0B33',
    // '--amber-transparent-30': '#F59E0B4D',
    // '--amber-transparent-40': '#F59E0B66',
    // '--amber-transparent-50': '#F59E0B80',
    // '--amber-transparent-60': '#F59E0B99',
    // '--amber-transparent-70': '#F59E0BB2',
    // '--amber-transparent-80': '#F59E0BCC',
    // '--amber-transparent-90': '#F59E0BE5',
    // '--amber-transparent-100': '#F59E0BFF',
    // '--amber-100': '#FEF3C7FF',
    // '--amber-200': '#FDE68AFF',
    // '--amber-300': '#FDE047FF',
    // '--amber-400': '#FACC15FF',
    // '--amber-500': '#F59E0BFF',
    // '--amber-600': '#D97706FF',
    // '--amber-700': '#B45309FF',
    // '--amber-800': '#92400EFF',
    // '--amber-900': '#78350FFF',
    // '--amber-950': '#422006FF',
    // '--yellow-50': '#FEFCE8FF',
    // '--yellow-100': '#FEF9C3FF',
    // '--yellow-200': '#FEF08AFF',
    // '--yellow-300': '#FDE047FF',
    // '--yellow-400': '#FACC15FF',
    // '--yellow-500': '#EAB308FF',
    // '--yellow-600': '#CA8A04FF',
    // '--yellow-700': '#A16207FF',
    // '--yellow-800': '#854D0EFF',
    // '--yellow-900': '#713F12FF',
    // '--yellow-950': '#422006FF',
    // '--yellow-transparent-05': '#EAB3080D',
    // '--yellow-transparent-10': '#EAB3081A',
    // '--yellow-transparent-20': '#EAB30833',
    // '--yellow-transparent-30': '#EAB3084D',
    // '--yellow-transparent-40': '#EAB30866',
    // '--yellow-transparent-50': '#EAB30880',
    // '--yellow-transparent-60': '#EAB30899',
    // '--yellow-transparent-70': '#EAB308B2',
    // '--yellow-transparent-80': '#EAB308CC',
    // '--yellow-transparent-90': '#EAB308E5',
    // '--yellow-transparent-100': '#EAB308FF',
    // '--lime-50': '#F7FEE7FF',
    // '--lime-100': '#ECFCCBFF',
    // '--lime-200': '#D9F99DFF',
    // '--lime-300': '#BEF264FF',
    // '--lime-400': '#A3E635FF',
    // '--lime-500': '#84CC16FF',
    // '--lime-600': '#65A30DFF',
    // '--lime-700': '#4D7C0FFF',
    // '--lime-800': '#3F6212FF',
    // '--lime-900': '#365314FF',
    // '--lime-950': '#1A2E05FF',
    // '--lime-transparent-05': '#84CC160D',
    // '--lime-transparent-10': '#84CC161A',
    // '--lime-transparent-20': '#84CC1633',
    // '--lime-transparent-30': '#84CC164D',
    // '--lime-transparent-40': '#84CC1666',
    // '--lime-transparent-50': '#84CC1680',
    // '--lime-transparent-60': '#84CC1699',
    // '--lime-transparent-70': '#84CC16B2',
    // '--lime-transparent-80': '#84CC16CC',
    // '--lime-transparent-90': '#84CC16E5',
    // '--lime-transparent-100': '#84CC16FF',
    // '--green-50': '#F0FDF4FF',
    // '--green-100': '#DCFCE7FF',
    // '--green-200': '#BBF7D0FF',
    // '--green-300': '#86EFACFF',
    // '--green-400': '#4ADE80FF',
    // '--green-500': '#22C55EFF',
    // '--green-600': '#16A34AFF',
    // '--green-700': '#15803DFF',
    // '--green-800': '#166534FF',
    // '--green-900': '#14532DFF',
    // '--green-950': '#052E16FF',
    // '--green-transparent-05': '#22C55E0D',
    // '--green-transparent-10': '#22C55E1A',
    // '--green-transparent-20': '#22C55E33',
    // '--green-transparent-30': '#22C55E4D',
    // '--green-transparent-40': '#22C55E66',
    // '--green-transparent-50': '#22C55E80',
    // '--green-transparent-60': '#22C55E99',
    // '--green-transparent-70': '#22C55EB2',
    // '--green-transparent-80': '#22C55ECC',
    // '--green-transparent-90': '#22C55EE5',
    // '--green-transparent-100': '#22C55EFF',
    // '--emerald-50': '#ECFDF5FF',
    // '--emerald-transparent-05': '#10B9810D',
    // '--emerald-transparent-10': '#10B9811A',
    // '--emerald-transparent-20': '#10B98133',
    // '--emerald-transparent-30': '#10B9814D',
    // '--emerald-transparent-40': '#10B98166',
    // '--emerald-transparent-50': '#10B98180',
    // '--emerald-transparent-60': '#10B98199',
    // '--emerald-transparent-70': '#10B981B2',
    // '--emerald-transparent-80': '#10B981CC',
    // '--emerald-transparent-90': '#10B981E5',
    // '--emerald-transparent-100': '#10B981FF',
    // '--emerald-100': '#D1FAE5FF',
    // '--emerald-200': '#A7F3D0FF',
    // '--emerald-300': '#6EE7B7FF',
    // '--emerald-400': '#34D399FF',
    // '--emerald-500': '#10B981FF',
    // '--emerald-600': '#059669FF',
    // '--emerald-700': '#047857FF',
    // '--emerald-800': '#065F46FF',
    // '--emerald-900': '#134E4AFF',
    // '--emerald-950': '#022C22FF',
    // '--teal-50': '#F0FDFAFF',
    // '--teal-100': '#CCFBF1FF',
    // '--teal-200': '#99F6E4FF',
    // '--teal-300': '#5EEAD4FF',
    // '--teal-400': '#2DD4BFFF',
    // '--teal-500': '#14B8A6FF',
    // '--teal-600': '#0D9488FF',
    // '--teal-700': '#0F766EFF',
    // '--teal-800': '#115E59FF',
    // '--teal-900': '#134E4AFF',
    // '--teal-950': '#042F2EFF',
    // '--teal-transparent-05': '#14B8A60D',
    // '--teal-transparent-10': '#14B8A61A',
    // '--teal-transparent-20': '#14B8A633',
    // '--teal-transparent-30': '#14B8A64D',
    // '--teal-transparent-40': '#14B8A666',
    // '--teal-transparent-50': '#14B8A680',
    // '--teal-transparent-60': '#14B8A699',
    // '--teal-transparent-70': '#14B8A6B2',
    // '--teal-transparent-80': '#14B8A6CC',
    // '--teal-transparent-90': '#14B8A6E5',
    // '--teal-transparent-100': '#14B8A6FF',
    // '--cyan-50': '#ECFEFFFF',
    // '--cyan-100': '#CFFAFEFF',
    // '--cyan-200': '#A5F3FCFF',
    // '--cyan-300': '#67E8F9FF',
    // '--cyan-400': '#22D3EEFF',
    // '--cyan-500': '#06B6D4FF',
    // '--cyan-600': '#0891B2FF',
    // '--cyan-700': '#0E7490FF',
    // '--cyan-800': '#155E75FF',
    // '--cyan-900': '#164E63FF',
    // '--cyan-950': '#083344FF',
    // '--cyan-transparent-05': '#06B6D40D',
    // '--cyan-transparent-10': '#06B6D41A',
    // '--cyan-transparent-20': '#06B6D433',
    // '--cyan-transparent-30': '#06B6D44D',
    // '--cyan-transparent-40': '#06B6D466',
    // '--cyan-transparent-50': '#06B6D480',
    // '--cyan-transparent-60': '#06B6D499',
    // '--cyan-transparent-70': '#06B6D4B2',
    // '--cyan-transparent-80': '#06B6D4CC',
    // '--cyan-transparent-90': '#06B6D4E5',
    // '--cyan-transparent-100': '#06B6D4FF',
    // '--sky-50': '#F0F9FFFF',
    // '--sky-transparent-05': '#0EA5E90D',
    // '--sky-transparent-10': '#0EA5E91A',
    // '--sky-transparent-20': '#0EA5E933',
    // '--sky-transparent-30': '#0EA5E94D',
    // '--sky-transparent-40': '#0EA5E966',
    // '--sky-transparent-50': '#0EA5E980',
    // '--sky-transparent-60': '#0EA5E999',
    // '--sky-transparent-70': '#0EA5E9B2',
    // '--sky-transparent-80': '#0EA5E9CC',
    // '--sky-transparent-90': '#0EA5E9E5',
    // '--sky-transparent-100': '#0EA5E9FF',
    // '--sky-100': '#E0F2FEFF',
    // '--sky-200': '#BAE6FDFF',
    // '--sky-300': '#7DD3FCFF',
    // '--sky-400': '#38BDF8FF',
    // '--sky-500': '#0EA5E9FF',
    // '--sky-600': '#0284C7FF',
    // '--sky-700': '#0369A1FF',
    // '--sky-800': '#075985FF',
    // '--sky-900': '#0C4A6EFF',
    // '--sky-950': '#082F49FF',
    // '--blue-50': '#EFF6FFFF',
    // '--blue-transparent-05': '#3B82F60D',
    // '--blue-transparent-10': '#3B82F61A',
    // '--blue-transparent-20': '#3B82F633',
    // '--blue-transparent-30': '#3B82F64D',
    // '--blue-transparent-40': '#3B82F666',
    // '--blue-transparent-50': '#3B82F680',
    // '--blue-transparent-60': '#3B82F699',
    // '--blue-transparent-70': '#3B82F6B2',
    // '--blue-transparent-80': '#3B82F6CC',
    // '--blue-transparent-90': '#3B82F6E5',
    // '--blue-transparent-100': '#3B82F6FF',
    // '--blue-100': '#DBEAFEFF',
    // '--blue-200': '#BFDBFEFF',
    // '--blue-300': '#93C5FDFF',
    // '--blue-400': '#60A5FAFF',
    // '--blue-500': '#3B82F6FF',
    // '--blue-600': '#2563EBFF',
    // '--blue-700': '#1D4ED8FF',
    // '--blue-800': '#1E40AFFF',
    // '--blue-900': '#1E3A8AFF',
    // '--blue-950': '#172554FF',
    // '--indigo-50': '#EEF2FFFF',
    // '--indigo-transparent-05': '#6366F10D',
    // '--indigo-transparent-10': '#6366F11A',
    // '--indigo-transparent-20': '#6366F133',
    // '--indigo-transparent-30': '#6366F14D',
    // '--indigo-transparent-40': '#6366F166',
    // '--indigo-transparent-50': '#6366F180',
    // '--indigo-transparent-60': '#6366F199',
    // '--indigo-transparent-70': '#6366F1B2',
    // '--indigo-transparent-80': '#6366F1CC',
    // '--indigo-transparent-90': '#6366F1E5',
    // '--indigo-transparent-100': '#6366F1FF',
    // '--indigo-100': '#E0E7FFFF',
    // '--indigo-200': '#C7D2FEFF',
    // '--indigo-300': '#A5B4FCFF',
    // '--indigo-400': '#818CF8FF',
    // '--indigo-500': '#6366F1FF',
    // '--indigo-600': '#4F46E5FF',
    // '--indigo-700': '#4338CAFF',
    // '--indigo-800': '#3730A3FF',
    // '--indigo-900': '#312E81FF',
    // '--indigo-950': '#1E1B4BFF',
    // '--violet-50': '#F5F3FFFF',
    // '--violet-transparent-05': '#8B5CF60D',
    // '--violet-transparent-10': '#8B5CF61A',
    // '--violet-transparent-20': '#8B5CF633',
    // '--violet-transparent-30': '#8B5CF64D',
    // '--violet-transparent-40': '#8B5CF666',
    // '--violet-transparent-50': '#8B5CF680',
    // '--violet-transparent-60': '#8B5CF699',
    // '--violet-transparent-70': '#8B5CF6B2',
    // '--violet-transparent-80': '#8B5CF6CC',
    // '--violet-transparent-90': '#8B5CF6E5',
    // '--violet-transparent-100': '#8B5CF6FF',
    // '--violet-100': '#EDE9FEFF',
    // '--violet-200': '#DDD6FEFF',
    // '--violet-300': '#C4B5FDFF',
    // '--violet-400': '#A78BFAFF',
    // '--violet-500': '#8B5CF6FF',
    // '--violet-600': '#7C3AEDFF',
    // '--violet-700': '#6D28D9FF',
    // '--violet-800': '#5B21B6FF',
    // '--violet-900': '#4C1D95FF',
    // '--violet-950': '#3B0764FF',
    // '--purple-50': '#FAF5FFFF',
    // '--purple-100': '#F3E8FFFF',
    // '--purple-200': '#E9D5FFFF',
    // '--purple-300': '#D8B4FEFF',
    // '--purple-400': '#C084FCFF',
    // '--purple-500': '#A855F7FF',
    // '--purple-600': '#9333EAFF',
    // '--purple-700': '#7E22CEFF',
    // '--purple-800': '#6B21A8FF',
    // '--purple-900': '#581C87FF',
    // '--purple-950': '#3B0764FF',
    // '--purple-transparent-05': '#A855F70D',
    // '--purple-transparent-10': '#A855F71A',
    // '--purple-transparent-20': '#A855F733',
    // '--purple-transparent-30': '#A855F74D',
    // '--purple-transparent-40': '#A855F766',
    // '--purple-transparent-50': '#A855F780',
    // '--purple-transparent-60': '#A855F799',
    // '--purple-transparent-70': '#A855F7B2',
    // '--purple-transparent-80': '#A855F7CC',
    // '--purple-transparent-90': '#A855F7E5',
    // '--purple-transparent-100': '#A855F7FF',
    // '--fuschia-50': '#FDF4FFFF',
    // '--fuschia-transparent-05': '#A855F70D',
    // '--fuschia-transparent-10': '#A855F71A',
    // '--fuschia-transparent-20': '#A855F733',
    // '--fuschia-transparent-30': '#A855F74D',
    // '--fuschia-transparent-40': '#A855F766',
    // '--fuschia-transparent-50': '#A855F780',
    // '--fuschia-transparent-60': '#A855F799',
    // '--fuschia-transparent-70': '#A855F7B2',
    // '--fuschia-transparent-80': '#A855F7CC',
    // '--fuschia-transparent-90': '#A855F7E5',
    // '--fuschia-transparent-100': '#A855F7FF',
    // '--pink-50': '#FDF2F8FF',
    // '--pink-transparent-05': '#EC48990D',
    // '--pink-transparent-10': '#EC48991A',
    // '--pink-transparent-20': '#EC489933',
    // '--pink-transparent-30': '#EC48994D',
    // '--pink-transparent-40': '#EC489966',
    // '--pink-transparent-50': '#EC489980',
    // '--pink-transparent-60': '#EC489999',
    // '--pink-transparent-70': '#EC4899B2',
    // '--pink-transparent-80': '#EC4899CC',
    // '--pink-transparent-90': '#EC4899E5',
    // '--pink-transparent-100': '#EC4899FF',
    // '--pink-100': '#FCE7F3FF',
    // '--pink-200': '#FBCFE8FF',
    // '--pink-300': '#F9A8D4FF',
    // '--pink-400': '#F472B6FF',
    // '--pink-500': '#EC4899FF',
    // '--pink-600': '#DB2777FF',
    // '--pink-700': '#BE185DFF',
    // '--pink-800': '#9D174DFF',
    // '--pink-900': '#831843FF',
    // '--pink-950': '#500724FF',
    // '--fuschia-100': '#FAE8FFFF',
    // '--fuschia-200': '#F5D0FEFF',
    // '--fuschia-300': '#F0ABFCFF',
    // '--fuschia-400': '#E879F9FF',
    // '--fuschia-500': '#D946EFFF',
    // '--fuschia-600': '#C026D3FF',
    // '--fuschia-700': '#A21CAFFF',
    // '--fuschia-800': '#86198FFF',
    // '--fuschia-900': '#701A75FF',
    // '--fuschia-950': '#4A044EFF',
    // '--rose-50': '#FFF1F2FF',
    // '--rose-transparent-05': '#F43F5E0D',
    // '--rose-transparent-10': '#F43F5E1A',
    // '--rose-transparent-20': '#F43F5E33',
    // '--rose-transparent-30': '#F43F5E4D',
    // '--rose-transparent-40': '#F43F5E66',
    // '--rose-transparent-50': '#F43F5E80',
    // '--rose-transparent-60': '#F43F5E99',
    // '--rose-transparent-70': '#F43F5EB2',
    // '--rose-transparent-80': '#F43F5ECC',
    // '--rose-transparent-90': '#F43F5EE5',
    // '--rose-transparent-100': '#F43F5EFF',
    // '--zinc-50': '#FAFAFAFF',
    // '--zinc-100': '#F4F4F5FF',
    // '--zinc-200': '#E4E4E7FF',
    // '--zinc-300': '#D4D4D8FF',
    // '--zinc-400': '#A1A1AAFF',
    // '--zinc-500': '#71717AFF',
    // '--zinc-600': '#52525BFF',
    // '--zinc-700': '#3F3F46FF',
    // '--zinc-800': '#27272AFF',
    // '--zinc-900': '#18181BFF',
    // '--zinc-950': '#09090BFF',
    // '--zinc-transparent-05': '#71717A0D',
    // '--zinc-transparent-10': '#71717A1A',
    // '--zinc-transparent-20': '#71717A33',
    // '--zinc-transparent-30': '#71717A4D',
    // '--zinc-transparent-40': '#71717A66',
    // '--zinc-transparent-50': '#71717A80',
    // '--zinc-transparent-60': '#71717A99',
    // '--zinc-transparent-70': '#71717AB2',
    // '--zinc-transparent-80': '#71717ACC',
    // '--zinc-transparent-90': '#71717AE5',
    // '--zinc-transparent-100': '#71717AFF',
    // '--stone-50': '#FAFAF9FF',
    // '--stone-100': '#F5F5F4FF',
    // '--stone-200': '#E7E5E4FF',
    // '--stone-300': '#D6D3D1FF',
    // '--stone-400': '#A8A29EFF',
    // '--stone-500': '#78716CFF',
    // '--stone-600': '#57534EFF',
    // '--stone-700': '#44403CFF',
    // '--stone-800': '#292524FF',
    // '--stone-900': '#1C1917FF',
    // '--stone-950': '#0C0A09FF',
    // '--stone-transparent-05': '#78716C0D',
    // '--stone-transparent-10': '#78716C1A',
    // '--stone-transparent-20': '#78716C33',
    // '--stone-transparent-30': '#78716C4D',
    // '--stone-transparent-40': '#78716C66',
    // '--stone-transparent-50': '#78716C80',
    // '--stone-transparent-60': '#78716C99',
    // '--stone-transparent-70': '#78716CB2',
    // '--stone-transparent-80': '#78716CCC',
    // '--stone-transparent-90': '#78716CE5',
    // '--stone-transparent-100': '#78716CFF',
    // '--slate-50': '#F8FAFCFF',
    // '--slate-100': '#F1F5F9FF',
    // '--slate-200': '#E2E8F0FF',
    // '--slate-300': '#CBD5E1FF',
    // '--slate-400': '#94A3B8FF',
    // '--slate-500': '#64748BFF',
    // '--slate-600': '#475569FF',
    // '--slate-700': '#334155FF',
    // '--slate-800': '#1E293BFF',
    // '--slate-900': '#0F172AFF',
    // '--slate-950': '#020617FF',
    // '--slate-transparent-05': '#64748B0D',
    // '--slate-transparent-10': '#64748B1A',
    // '--slate-transparent-20': '#64748B33',
    // '--slate-transparent-30': '#64748B4D',
    // '--slate-transparent-40': '#64748B66',
    // '--slate-transparent-50': '#64748B80',
    // '--slate-transparent-60': '#64748B99',
    // '--slate-transparent-70': '#64748BB2',
    // '--slate-transparent-80': '#64748BCC',
    // '--slate-transparent-90': '#64748BE5',
    // '--slate-transparent-100': '#64748BFF',
    // '--rose-100': '#FFE4E6FF',
    // '--rose-200': '#FECDD3FF',
    // '--rose-300': '#FDA4AFFF',
    // '--rose-400': '#FB7185FF',
    // '--rose-500': '#F43F5EFF',
    // '--rose-600': '#E11D48FF',
    // '--rose-700': '#BE123CFF',
    // '--rose-800': '#9F1239FF',
    // '--rose-900': '#881337FF',
    // '--rose-950': '#4C0519FF',
  };

  variables = signal<ThemeVariable[]>([]);
  isOpen = signal(false);

  groups = computed(() => {
    const vars = this.variables();
    const groupNames = Array.from(new Set(vars.map((v) => v.group)));
    return groupNames;
  });

  constructor() {
    this.init();
  }

  private init() {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    const savedVars = saved ? JSON.parse(saved) : {};

    // Base variables from initialData
    const vars: ThemeVariable[] = Object.entries(this.initialData).map(([name, value]) => {
      const currentValue = savedVars[name] || value;
      if (savedVars[name]) {
        document.documentElement.style.setProperty(name, currentValue);
      }
      return {
        name,
        value: currentValue,
        originalValue: value,
        group: this.extractGroup(name),
        isColor: value.startsWith('#'),
      };
    });

    // Handle variables in localStorage that are NOT in initialData (Custom variables)
    Object.entries(savedVars).forEach(([name, value]: [string, any]) => {
      const exists = vars.some((v) => v.name === name);
      if (!exists) {
        document.documentElement.style.setProperty(name, value);
        vars.push({
          name,
          value,
          originalValue: value, // For custom vars, original is current
          group: this.extractGroup(name),
          isColor: (value as string).startsWith('#'),
          isCustom: true,
        });
      }
    });

    this.variables.set(vars);
  }
  getChangesScss(): string {
    const changes = this.variables().filter((v) => v.value !== v.originalValue || v.isCustom);
    if (changes.length === 0) return '/* No changes yet */\n:root {\n\n}';

    let scss = '/* color-primitives - Exported Changes */\n:root {\n';
    changes.forEach((v) => {
      scss += `  ${v.name}: ${v.value};\n`;
    });
    scss += '}';
    return scss;
  }

  applyScssChanges(scss: string) {
    const regex = /(--[a-zA-Z0-9_-]+)\s*:\s*([^;]+);/g;
    let match;
    const newChanges: Record<string, string> = {};

    while ((match = regex.exec(scss)) !== null) {
      const name = match[1].trim();
      const value = match[2].trim();
      newChanges[name] = value;
    }

    this.variables.update((vars) => {
      let next = vars
        .map((v) => {
          if (newChanges[v.name]) {
            const newVal = newChanges[v.name];
            document.documentElement.style.setProperty(v.name, newVal);
            delete newChanges[v.name]; // Consume it
            return { ...v, value: newVal };
          } else {
            // It's NOT in the text anymore
            if (v.isCustom) {
              // Remove completely from DOM and signal
              document.documentElement.style.removeProperty(v.name);
              return null;
            } else if (v.value !== v.originalValue) {
              // Theme variable: Reset to original
              document.documentElement.style.setProperty(v.name, v.originalValue);
              return { ...v, value: v.originalValue };
            }
            return v;
          }
        })
        .filter((v) => v !== null) as ThemeVariable[];

      // What's left in newChanges are brand new custom variables
      Object.entries(newChanges).forEach(([name, value]) => {
        document.documentElement.style.setProperty(name, value);
        next.push({
          name,
          value,
          originalValue: value,
          group: this.extractGroup(name),
          isColor: value.startsWith('#'),
          isCustom: true,
        });
      });

      this.saveToStorageInternal(next);
      return next;
    });
  }

  addCustomVariable(name: string, value: string) {
    if (!name.startsWith('--')) name = '--' + name;

    this.variables.update((vars) => {
      const exists = vars.find((v) => v.name === name);
      if (exists) return vars; // Prevent duplicates

      const newVar = {
        name,
        value,
        originalValue: value,
        group: this.extractGroup(name),
        isColor: value.startsWith('#'),
        isCustom: true,
      };

      document.documentElement.style.setProperty(name, value);
      const next = [...vars, newVar];
      this.saveToStorageInternal(next);
      return next;
    });
  }

  private saveToStorageInternal(currentVars: ThemeVariable[]) {
    const changes: Record<string, string> = {};
    currentVars.forEach((v) => {
      if (v.value !== v.originalValue || v.isCustom) {
        changes[v.name] = v.value;
      }
    });
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(changes));
  }

  private extractGroup(name: string): string {
    const parts = name.replace('--', '').split('-');
    if (parts[0] === 'theme' && parts[1] === 'transparent') return 'theme-transparent';
    if (parts[1] === 'transparent') return parts[0] + '-transparent';

    // Check if it belongs to initialData groups
    const families = [
      'theme',
      'neutral',
      'white',
      'black',
      'gray',
      'red',
      'orange',
      'amber',
      'yellow',
      'lime',
      'green',
      'emerald',
      'teal',
      'cyan',
      'sky',
      'blue',
      'indigo',
      'violet',
      'purple',
      'fuschia',
      'pink',
      'rose',
      'zinc',
      'stone',
      'slate',
    ];
    if (families.includes(parts[0])) return parts[0];

    return 'custom';
  }

  updateVariable(name: string, value: string) {
    this.variables.update((vars) => {
      const next = vars.map((v) => (v.name === name ? { ...v, value } : v));
      this.saveToStorageInternal(next);
      return next;
    });
    document.documentElement.style.setProperty(name, value);
  }

  resetVariable(name: string) {
    const v = this.variables().find((x) => x.name === name);
    if (v) {
      this.updateVariable(name, v.originalValue);
    }
  }

  generateShades(group: string, baseColorHex: string) {
    try {
      if (!baseColorHex) return;
      const baseColor = Color.from(baseColorHex);
      if (!baseColor) return;

      const rgba = baseColor.getRgba();
      const r = rgba.red;
      const g = rgba.green;
      const b = rgba.blue;
      const a = rgba.alpha;

      const mix = (start: number, end: number, t: number) => Math.round(start + (end - start) * t);

      if (group.includes('transparent')) {
        // Opacity Scale (hex alpha)
        const opacities = [
          { key: '05', alpha: 0.05 },
          { key: '10', alpha: 0.1 },
          { key: '20', alpha: 0.2 },
          { key: '30', alpha: 0.3 },
          { key: '40', alpha: 0.4 },
          { key: '50', alpha: 0.5 },
          { key: '60', alpha: 0.6 },
          { key: '70', alpha: 0.7 },
          { key: '80', alpha: 0.8 },
          { key: '90', alpha: 0.9 },
          { key: '100', alpha: 1.0 },
        ];

        opacities.forEach((item) => {
          const varName = `--${group}-${item.key}`;
          const found = this.variables().find((v) => v.name === varName);
          if (!found) return;

          const shade = new Color().setRgba(r, g, b, item.alpha);
          this.updateVariable(varName, shade.toHexString(true));
        });
      } else {
        // Color Shade Scale (lerp towards white/black)
        const levels = [
          { key: '50', t: 0.92, isLight: true },
          { key: '100', t: 0.8, isLight: true },
          { key: '200', t: 0.6, isLight: true },
          { key: '300', t: 0.4, isLight: true },
          { key: '400', t: 0.2, isLight: true },
          { key: '500', t: 0, isLight: false }, // Base
          { key: '600', t: 0.2, isLight: false },
          { key: '700', t: 0.4, isLight: false },
          { key: '800', t: 0.6, isLight: false },
          { key: '900', t: 0.8, isLight: false },
          { key: '950', t: 0.92, isLight: false },
        ];

        levels.forEach((item) => {
          const varName = `--${group}-${item.key}`;
          const found = this.variables().find((v) => v.name === varName);
          if (!found) return;

          if (item.key === '500') {
            this.updateVariable(varName, baseColorHex);
            return;
          }

          let newR, newG, newB;
          if (item.isLight) {
            // Lerp towards White (255)
            newR = mix(r, 255, item.t);
            newG = mix(g, 255, item.t);
            newB = mix(b, 255, item.t);
          } else {
            // Lerp towards Black (0)
            newR = mix(r, 0, item.t);
            newG = mix(g, 0, item.t);
            newB = mix(b, 0, item.t);
          }

          const shade = new Color().setRgba(newR, newG, newB, a);
          this.updateVariable(varName, shade.toHexString(true));
        });
      }
    } catch (e) {
      console.error('Error generating shades:', e);
    }
  }

  reset() {
    this.variables().forEach((v) => {
      document.documentElement.style.removeProperty(v.name);
    });
    localStorage.removeItem(this.STORAGE_KEY);
    this.init();
  }

  toggle() {
    this.isOpen.update((v) => !v);
  }

  exportScss() {
    let scss = '/* color-primitives - Exported Changes */\n:root {\n';
    this.variables().forEach((v) => {
      if (v.value !== v.originalValue || v.isCustom) {
        scss += `  ${v.name}: ${v.value};\n`;
      }
    });
    scss += '}';

    const blob = new Blob([scss], { type: 'text/scss' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tokens-primitives-changes.scss';
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
